import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Camera, Edit, Trash2, Plus, Upload, Star, MoreVertical, X, FileCheck, Clock, CheckCircle, XCircle, BarChart3, ThumbsUp, ThumbsDown, Filter, Eye, Flag } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { supabase, Gallery, GalleryPhoto, GalleryAllImage, getAuthenticatedStorage } from '@/lib/supabase';
import { GalleryDataAccess } from '@/lib/galleryDataAccess';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { ApprovalFlow } from '@/components/navigation/ApprovalFlow';
import { useApprovalCounts } from '@/hooks/useApprovalCounts';

// Import gallery functions
const fetchGallery = async (id: number): Promise<Gallery> => {
  const { data, error } = await supabase
    .from('galleries')
    .select('*')
    .eq('id', id)
    .single();
  
  if (error) throw error;
  return data;
};

const fetchGalleryPhotos = async (galleryId: number): Promise<GalleryPhoto[]> => {
  const { data, error } = await supabase
    .from('gallery_photos')
    .select('*')
    .eq('gallery_id', galleryId)
    .order('sort_order', { ascending: true });
  
  if (error) throw error;
  return data || [];
};

// Unified function to fetch all images (both direct photos and job images)
const fetchGalleryAllImages = async (galleryId: number): Promise<GalleryAllImage[]> => {
  try {
    // First, try to query the unified view if it exists
    const { data: viewData, error: viewError } = await supabase
      .from('gallery_all_images')
      .select('*')
      .eq('gallery_id', galleryId)
      .order('created_at', { ascending: false });
    
    if (!viewError && viewData) {
      return viewData;
    }
    
    // Fallback: If unified view doesn't exist, query gallery_photos only
    // This maintains backward compatibility
    console.log('Unified view not available, falling back to gallery_photos only');
    const { data: photosData, error: photosError } = await supabase
      .from('gallery_photos')
      .select('*')
      .eq('gallery_id', galleryId)
      .order('sort_order', { ascending: true });
    
    if (photosError) throw photosError;
    
    // Transform gallery_photos to match GalleryAllImage interface
    const transformedPhotos: GalleryAllImage[] = (photosData || []).map(photo => ({
      image_type: 'gallery_photo' as const,
      original_id: photo.id,
      gallery_id: photo.gallery_id,
      image_url: photo.image_url,
      caption: photo.caption,
      review_status: 'approved' as const, // Direct uploads are always approved
      created_at: photo.created_at,
      sort_order: photo.sort_order
    }));
    
    return transformedPhotos;
  } catch (error) {
    console.error('Error fetching gallery images:', error);
    throw error;
  }
};

const uploadGalleryImage = async (galleryId: number, file: File, caption?: string): Promise<GalleryPhoto> => {
  console.log('🔄 Uploading gallery image via unified system...');
  
  try {
    // Use unified data access layer for upload
    const unifiedImage = await GalleryDataAccess.uploadGalleryImage({
      galleryId,
      file,
      caption,
      franchiseRegion: 'general' // Default for direct uploads
    });
    
    console.log('✅ Image uploaded successfully via unified system');
    
    // Convert back to GalleryPhoto format for compatibility
    const legacyFormat: GalleryPhoto = {
      id: unifiedImage.original_id,
      gallery_id: unifiedImage.gallery_id,
      image_url: unifiedImage.image_url,
      caption: unifiedImage.caption,
      sort_order: unifiedImage.sort_order || 0,
      created_at: unifiedImage.created_at
    };
    
    return legacyFormat;
  } catch (error) {
    console.error('❌ Unified upload failed, falling back to legacy method:', error);
    
    // Fallback to legacy gallery_photos upload
    const { storage } = await getAuthenticatedStorage();
    
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `galleries/${galleryId}/${fileName}`;
    
    const { data: uploadData, error: uploadError } = await storage
      .from('gallery-images')
      .upload(filePath, file);
      
    if (uploadError) throw uploadError;
    
    const { data: urlData } = storage
      .from('gallery-images')
      .getPublicUrl(filePath);
      
    const { data, error: insertError } = await supabase
      .from('gallery_photos')
      .insert({
        gallery_id: galleryId,
        image_url: urlData.publicUrl,
        caption: caption || '',
        sort_order: 0
      })
      .select()
      .single();
      
    if (insertError) throw insertError;
    console.log('✅ Image uploaded via legacy fallback');
    return data;
  }
};

const deleteGalleryPhoto = async (photoId: number, imageType: 'job_image' | 'gallery_photo' = 'gallery_photo'): Promise<void> => {
  console.log(`🔄 Deleting ${imageType} with ID ${photoId}...`);
  
  try {
    // Use unified data access layer for deletion
    await GalleryDataAccess.deleteGalleryImage(photoId, imageType);
    console.log('✅ Image deleted successfully via unified system');
  } catch (error) {
    console.error('❌ Unified delete failed, falling back to legacy method:', error);
    
    // Fallback to legacy gallery_photos deletion
    const { error: deleteError } = await supabase
      .from('gallery_photos')
      .delete()
      .eq('id', photoId);
      
    if (deleteError) throw deleteError;
    console.log('✅ Image deleted via legacy fallback');
  }
};

const updatePhotoCaption = async (photoId: number, caption: string, imageType: 'job_image' | 'gallery_photo' = 'gallery_photo'): Promise<GalleryPhoto> => {
  console.log(`🔄 Updating caption for ${imageType} with ID ${photoId}...`);
  
  try {
    // Use unified data access layer for caption update
    await GalleryDataAccess.updateImageCaption(photoId, caption, imageType);
    console.log('✅ Caption updated successfully via unified system');
    
    // Return a compatible response (we don't have the full data, so construct it)
    const response: GalleryPhoto = {
      id: photoId,
      gallery_id: 0, // We don't have this info from the update
      image_url: '', // We don't have this info from the update
      caption: caption,
      sort_order: 0,
      created_at: new Date().toISOString()
    };
    
    return response;
  } catch (error) {
    console.error('❌ Unified caption update failed, falling back to legacy method:', error);
    
    // Fallback to legacy gallery_photos update
    const { data, error: updateError } = await supabase
      .from('gallery_photos')
      .update({ caption })
      .eq('id', photoId)
      .select()
      .single();
      
    if (updateError) throw updateError;
    console.log('✅ Caption updated via legacy fallback');
    return data;
  }
};

const setCoverImage = async (galleryId: number, imageUrl: string): Promise<Gallery> => {
  const { data, error } = await supabase
    .from('galleries')
    .update({ cover_image: imageUrl })
    .eq('id', galleryId)
    .select()
    .single();
    
  if (error) throw error;
  return data;
};

const franchiseRegions = [
  { value: 'HQ', label: 'Headquarters' },
  { value: 'north_york', label: 'North York' },
  { value: 'london', label: 'London' },
  { value: 'toronto', label: 'Toronto' },
  { value: 'hamilton', label: 'Hamilton' },
  { value: 'mississauga', label: 'Mississauga' },
  { value: 'brampton', label: 'Brampton' },
  { value: 'scarborough', label: 'Scarborough' },
  { value: 'general', label: 'General' }
] as const;

export default function GalleryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [editingCaption, setEditingCaption] = useState<{photoId: number, caption: string, imageType: 'job_image' | 'gallery_photo'} | null>(null);
  const [lightboxImage, setLightboxImage] = useState<GalleryAllImage | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<'overview' | 'approval'>('overview');
  const [approvalFilter, setApprovalFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: approvalCounts } = useApprovalCounts();
  
  const galleryId = id ? parseInt(id) : null;
  
  // Fetch gallery details
  const { data: gallery, isLoading: isGalleryLoading } = useQuery({
    queryKey: ['gallery', galleryId],
    queryFn: () => fetchGallery(galleryId!),
    enabled: !!galleryId
  });
  
  // Fetch all gallery images (both photos and job images)
  const { data: allImages = [], refetch: refetchImages } = useQuery({
    queryKey: ['gallery-all-images', galleryId],
    queryFn: () => fetchGalleryAllImages(galleryId!),
    enabled: !!galleryId
  });

  // Legacy photos query for backward compatibility with existing upload/delete functions
  const { data: photos = [], refetch: refetchPhotos } = useQuery({
    queryKey: ['gallery-photos', galleryId],
    queryFn: () => fetchGalleryPhotos(galleryId!),
    enabled: !!galleryId
  });

  // Calculate franchise image statistics
  const franchiseImageStats = React.useMemo(() => {
    if (!allImages) return { total: 0, pending: 0, approved: 0, rejected: 0, included: 0, excluded: 0 };
    
    const jobImages = allImages.filter(img => img.image_type === 'job_image');
    const total = jobImages.length;
    const pending = jobImages.filter(img => img.review_status === 'pending').length;
    const approved = jobImages.filter(img => img.review_status === 'approved').length;
    const rejected = jobImages.filter(img => img.review_status === 'rejected').length;
    const included = jobImages.filter(img => img.is_cover || img.sort_order !== null).length;
    const excluded = total - included;
    
    return { total, pending, approved, rejected, included, excluded };
  }, [allImages]);

  // Check if gallery is ready for social media use
  const galleryReadinessInfo = React.useMemo(() => {
    if (!allImages || !gallery) return { isReady: false, message: 'Loading...', hasApprovedImages: false };
    
    const approvedImages = allImages.filter(img => 
      img.review_status === 'approved' && (img.is_cover || img.sort_order !== null || img.image_type === 'gallery_photo')
    );
    const hasApprovedImages = approvedImages.length > 0;
    const hasPendingApprovals = franchiseImageStats.pending > 0;
    
    if (hasApprovedImages && !hasPendingApprovals) {
      return { 
        isReady: true, 
        message: `Ready for social media! ${approvedImages.length} approved image${approvedImages.length !== 1 ? 's' : ''} available.`,
        hasApprovedImages: true 
      };
    } else if (hasApprovedImages && hasPendingApprovals) {
      return { 
        isReady: true, 
        message: `Ready for social media! ${approvedImages.length} approved image${approvedImages.length !== 1 ? 's' : ''} available. ${franchiseImageStats.pending} image${franchiseImageStats.pending !== 1 ? 's' : ''} still pending review.`,
        hasApprovedImages: true 
      };
    } else if (hasPendingApprovals) {
      return { 
        isReady: false, 
        message: `${franchiseImageStats.pending} image${franchiseImageStats.pending !== 1 ? 's' : ''} pending approval. Approve images to make gallery ready for social media.`,
        hasApprovedImages: false 
      };
    } else {
      return { 
        isReady: false, 
        message: 'No approved images available for social media use.',
        hasApprovedImages: false 
      };
    }
  }, [allImages, gallery, franchiseImageStats]);

  // Filter franchise images based on approval status
  const filteredFranchiseImages = React.useMemo(() => {
    if (!allImages) return [];
    
    const jobImages = allImages.filter(img => img.image_type === 'job_image');
    
    if (approvalFilter === 'all') return jobImages;
    return jobImages.filter(img => img.review_status === approvalFilter);
  }, [allImages, approvalFilter]);
  
  // Upload image mutation
  const uploadImageMutation = useMutation({
    mutationFn: ({ file, caption }: { file: File; caption?: string }) =>
      uploadGalleryImage(galleryId!, file, caption),
    onSuccess: () => {
      refetchPhotos();
      refetchImages(); // Refetch the unified images view
      queryClient.invalidateQueries({ queryKey: ['gallery', galleryId] });
      toast({ title: 'Image uploaded successfully', message: 'The image has been added to your gallery', type: 'success' });
    },
    onError: (error) => {
      console.error('Upload error:', error);
      toast({ title: 'Upload failed', message: 'Please try again', type: 'error' });
    }
  });
  
  // Delete photo mutation
  const deletePhotoMutation = useMutation({
    mutationFn: ({ photoId, imageType }: { photoId: number; imageType: 'job_image' | 'gallery_photo' }) =>
      deleteGalleryPhoto(photoId, imageType),
    onSuccess: () => {
      refetchPhotos();
      refetchImages(); // Refetch the unified images view
      toast({ title: 'Photo deleted successfully', message: 'The image has been removed from your gallery', type: 'success' });
    }
  });
  
  // Update caption mutation
  const updateCaptionMutation = useMutation({
    mutationFn: ({ photoId, caption, imageType }: { photoId: number; caption: string; imageType: 'job_image' | 'gallery_photo' }) =>
      updatePhotoCaption(photoId, caption, imageType),
    onSuccess: () => {
      refetchPhotos();
      refetchImages(); // Refetch the unified images view
      setEditingCaption(null);
      toast({ title: 'Caption updated successfully', message: 'The image caption has been saved', type: 'success' });
    }
  });
  
  // Set cover image mutation
  const setCoverImageMutation = useMutation({
    mutationFn: ({ imageUrl }: { imageUrl: string }) =>
      setCoverImage(galleryId!, imageUrl),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery', galleryId] });
      toast({ title: 'Cover image updated successfully', message: 'This image is now the gallery cover', type: 'success' });
    }
  });

  // Update job image approval status
  const updateJobImageStatus = async (jobId: number, status: 'approved' | 'rejected'): Promise<void> => {
    const { error } = await supabase
      .from('franchise_image_jobs')
      .update({ review_status: status })
      .eq('id', jobId);
    
    if (error) throw error;
  };

  // Approve job image mutation
  const approveImageMutation = useMutation({
    mutationFn: (jobId: number) => updateJobImageStatus(jobId, 'approved'),
    onSuccess: () => {
      refetchImages(); // Refetch the unified images view
      queryClient.invalidateQueries({ queryKey: ['gallery', galleryId] });
      toast({ title: 'Image approved', message: 'The image has been approved for use', type: 'success' });
    },
    onError: (error) => {
      console.error('Approve error:', error);
      toast({ title: 'Approval failed', message: 'Please try again', type: 'error' });
    }
  });

  // Reject job image mutation
  const rejectImageMutation = useMutation({
    mutationFn: (jobId: number) => updateJobImageStatus(jobId, 'rejected'),
    onSuccess: () => {
      refetchImages(); // Refetch the unified images view
      queryClient.invalidateQueries({ queryKey: ['gallery', galleryId] });
      toast({ title: 'Image rejected', message: 'The image has been rejected', type: 'success' });
    },
    onError: (error) => {
      console.error('Reject error:', error);
      toast({ title: 'Rejection failed', message: 'Please try again', type: 'error' });
    }
  });
  
  const handleBack = () => {
    navigate('/media/galleries');
  };
  
  const handleEdit = () => {
    navigate(`/media/galleries/${galleryId}/edit`);
  };
  
  const handleFileUpload = (files: FileList | null) => {
    if (!files || !galleryId) return;
    
    const fileArray = Array.from(files);
    setUploadingFiles(fileArray);
    
    fileArray.forEach(file => {
      uploadImageMutation.mutate({
        file,
        caption: ''
      });
    });
    
    setTimeout(() => setUploadingFiles([]), 2000);
  };
  
  const handleDeletePhoto = (photoId: number, imageType: 'job_image' | 'gallery_photo' = 'gallery_photo') => {
    if (confirm('Are you sure you want to delete this photo?')) {
      deletePhotoMutation.mutate({ photoId, imageType });
    }
  };
  
  const handleSetCoverImage = (imageUrl: string) => {
    setCoverImageMutation.mutate({ imageUrl });
  };
  
  const handleCaptionEdit = (photoId: number, currentCaption: string, imageType: 'job_image' | 'gallery_photo' = 'gallery_photo') => {
    setEditingCaption({ photoId, caption: currentCaption || '', imageType });
  };
  
  const handleCaptionSave = () => {
    if (editingCaption) {
      updateCaptionMutation.mutate({
        photoId: editingCaption.photoId,
        caption: editingCaption.caption,
        imageType: editingCaption.imageType
      });
    }
  };

  const handleApproveImage = (jobId: number) => {
    approveImageMutation.mutate(jobId);
  };

  const handleRejectImage = (jobId: number) => {
    rejectImageMutation.mutate(jobId);
  };

  // Flag image for review
  const handleFlagImage = async (jobId: number) => {
    try {
      const { error } = await supabase
        .from('franchise_image_jobs')
        .update({ review_status: 'under_review' })
        .eq('id', jobId);
      
      if (error) throw error;
      
      refetchImages();
      toast({ 
        title: 'Image flagged for review', 
        message: 'The image has been marked for further review', 
        type: 'success' 
      });
    } catch (error) {
      console.error('Error flagging image:', error);
      toast({ 
        title: 'Failed to flag image', 
        message: 'Please try again', 
        type: 'error' 
      });
    }
  };

  // Toggle image inclusion in gallery
  const toggleImageInclusion = async (galleryJobImageId: number, currentInclusion: boolean) => {
    try {
      const { error } = await supabase
        .from('gallery_job_images')
        .update({ is_included: !currentInclusion })
        .eq('id', galleryJobImageId);
      
      if (error) throw error;
      
      refetchImages();
      toast({ 
        title: currentInclusion ? 'Image excluded from gallery' : 'Image included in gallery', 
        message: `The image is now ${currentInclusion ? 'excluded from' : 'included in'} this gallery`, 
        type: 'success' 
      });
    } catch (error) {
      console.error('Error toggling image inclusion:', error);
      toast({ 
        title: 'Failed to update image', 
        message: 'Please try again', 
        type: 'error' 
      });
    }
  };
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
    }
  };
  
  const getRegionBadge = (region: string | undefined) => {
    if (!region) return null;
    const regionInfo = franchiseRegions.find(r => r.value === region);
    return <Badge variant="outline">{regionInfo?.label || region}</Badge>;
  };

  const openLightbox = (image: GalleryAllImage) => {
    const imageIndex = allImages.findIndex(img => img.original_id === image.original_id && img.image_type === image.image_type);
    setLightboxIndex(imageIndex);
    setLightboxImage(image);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  const navigateLightbox = (direction: 'prev' | 'next') => {
    if (!lightboxImage || allImages.length === 0) return;
    
    let newIndex;
    if (direction === 'prev') {
      newIndex = lightboxIndex > 0 ? lightboxIndex - 1 : allImages.length - 1;
    } else {
      newIndex = lightboxIndex < allImages.length - 1 ? lightboxIndex + 1 : 0;
    }
    
    setLightboxIndex(newIndex);
    setLightboxImage(allImages[newIndex]);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!lightboxImage) return;
    
    switch (e.key) {
      case 'Escape':
        closeLightbox();
        break;
      case 'ArrowLeft':
        navigateLightbox('prev');
        break;
      case 'ArrowRight':
        navigateLightbox('next');
        break;
    }
  };

  React.useEffect(() => {
    if (lightboxImage) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [lightboxImage, lightboxIndex, allImages]);
  
  if (isGalleryLoading || !gallery) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6">
      {/* Breadcrumb Navigation */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Breadcrumb 
          items={[
            { label: 'Dashboard', href: '/', icon: ArrowLeft },
            { label: 'Gallery Management', href: '/media/galleries' },
            { label: gallery?.title || 'Gallery Details', isActive: true }
          ]}
        />
      </motion.div>

      {/* Approval Flow Navigation */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <ApprovalFlow 
          currentPage="gallery-detail"
          galleryId={id}
          pendingCount={approvalCounts?.totalPending || 0}
        />
      </motion.div>

      {/* Header */}
      <motion.div 
        className="flex justify-between items-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Galleries
          </Button>
          
          <div>
            <h1 className="text-3xl font-bold">{gallery.title}</h1>
            <div className="flex items-center gap-2 mt-1">
              {getRegionBadge(gallery.franchise_region)}
              {gallery.job_number && (
                <Badge variant="outline">Job #{gallery.job_number}</Badge>
              )}
              {getStatusBadge(gallery.review_status)}
            </div>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setIsUploadDialogOpen(true)}
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Photos
          </Button>
          <Button
            variant="outline"
            onClick={handleEdit}
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Gallery
          </Button>
        </div>
      </motion.div>
      
      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="overview">Gallery Overview</TabsTrigger>
          <TabsTrigger value="approval" className="relative">
            Image Approvals
            {franchiseImageStats.pending > 0 && (
              <Badge className="ml-2 bg-yellow-500 text-white text-xs">
                {franchiseImageStats.pending}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Gallery Information */}
          <Card>
            <CardHeader>
              <CardTitle>Gallery Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {gallery.description && (
                <div>
                  <h3 className="font-semibold text-sm text-gray-700 mb-2">Description</h3>
                  <p className="text-sm text-gray-600">{gallery.description}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <span className="text-sm font-medium">Total Images:</span>
                  <p className="text-lg font-bold">{allImages.length}</p>
                  <p className="text-xs text-gray-500">
                    {allImages.filter(img => img.image_type === 'gallery_photo').length} photos, {' '}
                    {allImages.filter(img => img.image_type === 'job_image').length} job images
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium">Public:</span>
                  <p className="text-sm">{gallery.is_public ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <span className="text-sm font-medium">Social Media:</span>
                  <p className="text-sm">{gallery.used_for_social_media ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <span className="text-sm font-medium">Created:</span>
                  <p className="text-sm">{new Date(gallery.created_on).toLocaleDateString()}</p>
                </div>
              </div>
              
              {/* Gallery Readiness Indicator */}
              <div className={`mt-4 p-3 rounded-lg border ${
                galleryReadinessInfo.isReady 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  {galleryReadinessInfo.isReady ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <Clock className="h-4 w-4 text-amber-600" />
                  )}
                  <span className={`text-sm font-medium ${
                    galleryReadinessInfo.isReady ? 'text-green-800' : 'text-amber-800'
                  }`}>
                    {galleryReadinessInfo.isReady ? 'Social Media Ready' : 'Pending Approval'}
                  </span>
                </div>
                <p className={`text-xs ${
                  galleryReadinessInfo.isReady ? 'text-green-700' : 'text-amber-700'
                }`}>
                  {galleryReadinessInfo.message}
                </p>
                {!galleryReadinessInfo.isReady && franchiseImageStats.pending > 0 && (
                  <div className="mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-amber-700 border-amber-300 hover:bg-amber-100"
                      onClick={() => setApprovalFilter('pending')}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Review Pending Images
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Images Grid - Now supporting both direct photos and job images */}
          <Card>
        <CardHeader>
          <CardTitle>
            All Images ({allImages.length})
            {allImages.length > 0 && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                {allImages.filter(img => img.image_type === 'gallery_photo').length} photos, {' '}
                {allImages.filter(img => img.image_type === 'job_image').length} job images
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {allImages.length === 0 ? (
            <div className="text-center py-8">
              <Camera className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 mb-2">No images in this gallery yet</p>
              <Button onClick={() => setIsUploadDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Upload First Photo
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {allImages.map((image) => {
                const isCoverImage = gallery?.cover_image === image.image_url;
                
                return (
                  <div key={`${image.image_type}-${image.original_id}`} className="relative group">
                    <div className="aspect-square relative">
                      <img
                        src={image.image_url}
                        alt={image.caption || `${image.image_type === 'job_image' ? 'Job' : 'Gallery'} image`}
                        className={`w-full h-full object-cover rounded-lg transition-all cursor-pointer hover:opacity-80 ${
                          isCoverImage ? 'ring-2 ring-blue-500' : ''
                        }`}
                        onClick={() => openLightbox(image)}
                        onError={(e) => {
                          e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDIwMCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04NSA2NEw5NSA3NEwxMTUgNTRMMTM1IDc0SDE1VjU0SDQ1VjY0SDg1WiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';
                        }}
                      />
                      
                      {/* Cover badge */}
                      {isCoverImage && (
                        <div className="absolute top-2 left-2">
                          <Badge className="bg-blue-500 text-white text-xs">
                            <Star className="w-3 h-3 mr-1" />
                            Cover
                          </Badge>
                        </div>
                      )}
                      
                      {/* Image type badge */}
                      <div className="absolute bottom-2 left-2">
                        <Badge 
                          variant={image.image_type === 'job_image' ? 'secondary' : 'outline'} 
                          className="text-xs"
                        >
                          {image.image_type === 'job_image' ? 'Job' : 'Direct'}
                        </Badge>
                        {image.image_type === 'job_image' && (
                          <Badge 
                            variant={
                              image.review_status === 'approved' ? 'default' : 
                              image.review_status === 'rejected' ? 'destructive' : 
                              'secondary'
                            }
                            className="text-xs ml-1"
                          >
                            {image.review_status}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="secondary" size="sm" className="h-6 w-6 p-0">
                              <MoreVertical className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleSetCoverImage(image.image_url)}>
                              <Star className="mr-2 h-4 w-4" />
                              Set as Cover
                            </DropdownMenuItem>
                            
                            {/* Show edit/delete for both image types */}
                            <>
                              <DropdownMenuItem onClick={() => handleCaptionEdit(image.original_id, image.caption || '', image.image_type)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit Caption
                              </DropdownMenuItem>
                              {/* Only allow deletion of gallery photos, not job images */}
                              {image.image_type === 'gallery_photo' && (
                                <DropdownMenuItem 
                                  onClick={() => handleDeletePhoto(image.original_id, image.image_type)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              )}
                            </>
                            
                            {/* Job image approval actions */}
                            {image.image_type === 'job_image' && (
                              <>
                                {image.review_status === 'pending' && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleApproveImage(image.original_id)}>
                                      <CheckCircle className="mr-2 h-4 w-4" />
                                      Approve Image
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      onClick={() => handleRejectImage(image.original_id)}
                                      className="text-red-600"
                                    >
                                      <XCircle className="mr-2 h-4 w-4" />
                                      Reject Image
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {image.review_status !== 'pending' && (
                                  <DropdownMenuItem disabled>
                                    <Eye className="mr-2 h-4 w-4" />
                                    Status: {image.review_status}
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      
                      {editingCaption?.photoId === image.original_id && (
                        <div className="absolute bottom-0 left-0 right-0 bg-white p-2 rounded-b-lg border-t">
                          <Input
                            value={editingCaption.caption}
                            onChange={(e) => setEditingCaption({ ...editingCaption, caption: e.target.value })}
                            placeholder="Add a caption..."
                            className="text-xs mb-2"
                            onKeyPress={(e) => e.key === 'Enter' && handleCaptionSave()}
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={handleCaptionSave} disabled={updateCaptionMutation.isPending}>
                              Save
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingCaption(null)}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Caption overlay for non-editing state */}
                      {!(editingCaption?.photoId === image.original_id) && image.caption && (
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent text-white text-xs p-2 rounded-b-lg">
                          {image.caption}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </TabsContent>

    <TabsContent value="approval" className="space-y-6">
      {/* Approval Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <FileCheck className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-gray-600">Total Images</p>
                <p className="text-2xl font-bold">{franchiseImageStats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold">{franchiseImageStats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-gray-600">Approved</p>
                <p className="text-2xl font-bold">{franchiseImageStats.approved}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5 text-purple-600" />
              <div>
                <p className="text-sm font-medium text-gray-600">Included</p>
                <p className="text-2xl font-bold">{franchiseImageStats.included}</p>
                <p className="text-xs text-gray-500">{franchiseImageStats.excluded} excluded</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Filter and Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <label className="text-sm font-medium">Filter by status:</label>
              </div>
              <div className="flex gap-2">
                {['all', 'pending', 'approved', 'rejected'].map((filter) => (
                  <Button
                    key={filter}
                    variant={approvalFilter === filter ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setApprovalFilter(filter as typeof approvalFilter)}
                  >
                    {filter === 'all' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                    {filter !== 'all' && (
                      <Badge className="ml-1 text-xs" variant="secondary">
                        {franchiseImageStats[filter as keyof typeof franchiseImageStats]}
                      </Badge>
                    )}
                  </Button>
                ))}
              </div>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/media/approval-queue?gallery=${galleryId}`)}
            >
              <Eye className="h-4 w-4 mr-2" />
              View in Approval Queue
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Franchise Images List */}
      <Card>
        <CardHeader>
          <CardTitle>
            Franchise Images ({filteredFranchiseImages.length})
            {franchiseImageStats.pending > 0 && (
              <Badge className="ml-2 bg-yellow-100 text-yellow-800">
                {franchiseImageStats.pending} pending approval
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredFranchiseImages.length === 0 ? (
            <div className="text-center py-8">
              <Camera className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 mb-2">
                {approvalFilter === 'all' 
                  ? 'No franchise images associated with this gallery'
                  : `No ${approvalFilter} franchise images`
                }
              </p>
              {approvalFilter !== 'all' && (
                <Button variant="outline" onClick={() => setApprovalFilter('all')}>
                  Show All Images
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredFranchiseImages.map((image) => {
                // image is already a GalleryAllImage, no need for .franchise_image_jobs
                if (!image) return null;
                
                return (
                  <div key={image.original_id} className="relative group border rounded-lg overflow-hidden">
                    <div className="aspect-square relative">
                      <img
                        src={image.image_url}
                        alt={image.caption || image.file_name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDIwMCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04NSA2NEw5NSA3NEwxMTUgNTRMMTM1IDc0SDE1VjU0SDQ1VjY0SDg1WiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';
                        }}
                      />
                      
                      {/* Status badges */}
                      <div className="absolute top-2 left-2 flex gap-1">
                        {getStatusBadge(image.review_status)}
                      </div>
                      
                      {/* Inclusion badge */}
                      <div className="absolute top-2 right-2">
                        <Badge 
                          variant={image.is_cover ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {image.is_cover ? 'Cover' : 'Included'}
                        </Badge>
                      </div>
                      
                      {/* Action overlay */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <TooltipProvider>
                          <div className="flex gap-3">
                            {image.review_status === 'pending' && (
                              <>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="lg"
                                      variant="outline"
                                      className="bg-white text-green-600 hover:bg-green-50 border-green-200 hover:border-green-300 h-12 w-12 p-0"
                                      onClick={() => handleApproveImage(image.job_id || image.original_id)}
                                    >
                                      <ThumbsUp className="w-6 h-6" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Approve this image for use in galleries and social media</p>
                                  </TooltipContent>
                                </Tooltip>
                                
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="lg"
                                      variant="outline"
                                      className="bg-white text-red-600 hover:bg-red-50 border-red-200 hover:border-red-300 h-12 w-12 p-0"
                                      onClick={() => handleRejectImage(image.job_id || image.original_id)}
                                    >
                                      <ThumbsDown className="w-6 h-6" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Reject this image - it will not be available for use</p>
                                  </TooltipContent>
                                </Tooltip>
                                
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="lg"
                                      variant="outline"
                                      className="bg-white text-orange-600 hover:bg-orange-50 border-orange-200 hover:border-orange-300 h-12 w-12 p-0"
                                      onClick={() => handleFlagImage(image.job_id || image.original_id)}
                                    >
                                      <Flag className="w-6 h-6" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Flag this image for further review</p>
                                  </TooltipContent>
                                </Tooltip>
                              </>
                            )}
                            
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="lg"
                                  variant="outline"
                                  className={`bg-white h-12 px-4 ${
                                    image.is_included 
                                      ? 'text-blue-600 border-blue-200 hover:bg-blue-50' 
                                      : 'text-gray-600 border-gray-200 hover:bg-gray-50'
                                  }`}
                                  onClick={() => toggleImageInclusion(image.original_id, image.is_included)}
                                >
                                  {image.is_included ? 'Exclude' : 'Include'}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>
                                  {image.is_included 
                                    ? 'Remove this image from the gallery display' 
                                    : 'Include this image in the gallery display'
                                  }
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TooltipProvider>
                      </div>
                    </div>
                    
                    {/* Image metadata */}
                    <div className="p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm truncate">{image.file_name}</h4>
                        <Badge variant="outline" className="text-xs">
                          Job #{image.job_number}
                        </Badge>
                      </div>
                      
                      {image.caption && (
                        <p className="text-xs text-gray-600 line-clamp-2">
                          {image.caption}
                        </p>
                      )}
                      
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{getRegionBadge(image.franchise_region)}</span>
                        <span>{new Date(image.created_at).toLocaleDateString()}</span>
                      </div>
                      
                      {/* Approval History */}
                      {image.review_status !== 'pending' && (
                        <div className="text-xs text-gray-500 pt-1 border-t">
                          <span className="flex items-center gap-1">
                            {image.review_status === 'approved' ? (
                              <CheckCircle className="w-3 h-3 text-green-600" />
                            ) : image.review_status === 'rejected' ? (
                              <XCircle className="w-3 h-3 text-red-600" />
                            ) : (
                              <Flag className="w-3 h-3 text-orange-600" />
                            )}
                            {image.review_status === 'approved' ? 'Approved' : 
                             image.review_status === 'rejected' ? 'Rejected' : 'Flagged'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </TabsContent>
    </Tabs>
      
      {/* Upload Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Photos</DialogTitle>
            <DialogDescription>
              Add new photos to this gallery
            </DialogDescription>
          </DialogHeader>
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileUpload(e.target.files)}
              className="hidden"
            />
            <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-600 mb-2">Drag and drop images here, or click to select files</p>
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadImageMutation.isPending}
            >
              {uploadImageMutation.isPending ? 'Uploading...' : 'Select Images'}
            </Button>
            {uploadingFiles.length > 0 && (
              <p className="text-xs text-blue-600 mt-2">Uploading {uploadingFiles.length} files...</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <div className="relative max-w-[95vw] max-h-[95vh] flex items-center justify-center">
            {/* Close button */}
            <Button
              variant="secondary"
              size="sm"
              className="absolute top-4 right-4 z-10 bg-black/20 hover:bg-black/40 text-white border-none"
              onClick={closeLightbox}
            >
              <X className="h-4 w-4" />
            </Button>

            {/* Previous button */}
            {allImages.length > 1 && (
              <Button
                variant="secondary"
                size="sm"
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/20 hover:bg-black/40 text-white border-none"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateLightbox('prev');
                }}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}

            {/* Next button */}
            {allImages.length > 1 && (
              <Button
                variant="secondary"
                size="sm"
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/20 hover:bg-black/40 text-white border-none"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateLightbox('next');
                }}
              >
                <ArrowLeft className="h-4 w-4 rotate-180" />
              </Button>
            )}

            {/* Main image */}
            <img
              src={lightboxImage.image_url}
              alt={lightboxImage.caption || `${lightboxImage.image_type === 'job_image' ? 'Job' : 'Gallery'} image`}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
              onError={(e) => {
                e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDIwMCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04NSA2NEw5NSA3NEwxMTUgNTRMMTM1IDc0SDE1VjU0SDQ1VjY0SDg1WiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';
              }}
            />

            {/* Image info overlay */}
            <div className="absolute bottom-4 left-4 right-4 bg-black/60 text-white p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge 
                      variant={lightboxImage.image_type === 'job_image' ? 'secondary' : 'outline'}
                      className="text-xs"
                    >
                      {lightboxImage.image_type === 'job_image' ? 'Job Image' : 'Direct Upload'}
                    </Badge>
                    {lightboxImage.image_type === 'job_image' && (
                      <Badge 
                        variant={
                          lightboxImage.review_status === 'approved' ? 'default' : 
                          lightboxImage.review_status === 'rejected' ? 'destructive' : 
                          'secondary'
                        }
                        className="text-xs"
                      >
                        {lightboxImage.review_status}
                      </Badge>
                    )}
                    {gallery?.cover_image === lightboxImage.image_url && (
                      <Badge className="bg-blue-500 text-white text-xs">
                        <Star className="w-3 h-3 mr-1" />
                        Cover
                      </Badge>
                    )}
                  </div>
                  {lightboxImage.caption && (
                    <p className="text-sm mb-1">{lightboxImage.caption}</p>
                  )}
                  <p className="text-xs text-gray-300">
                    Image {lightboxIndex + 1} of {allImages.length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}